import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddPayoutAccountIBANFormProps,
  IBANForm,
} from './AddPayoutAccountIBANForm.types';
import Typography from '../../sharedComponents/Typography/Typography';
import TextInput from '../../sharedComponents/TextInput/TextInput';
import {
  FORM_ID,
  hasNoEmptyFields,
  ibanFormValidator,
  LegalType as LT,
} from '../../utils/validate';
import styles from './add-payout-account-iban-form.module.scss';

const AddPayoutAccountIBANForm: React.FC<AddPayoutAccountIBANFormProps> = ({
  handleFormComplete,
  handleFormChange,
  recipientCountry,
  currency,
  allowUK = false,
}) => {
  const [ibanForm, setIbanForm] = useState<IBANForm>({
    legalType: LT.PRIVATE,
    accountHolderName: '',
    bic: '',
    iban: '',
    countryCode: recipientCountry, //will be passed from previous selection in the identification form
    city: '',
    addressLine: '',
    postalCode: '',
    currency,
  });
  const { t } = useTranslation();
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isIbanFocused, setIsIbanFocused] = useState(false);

  const formID = FORM_ID.IBAN;

  const handleFieldChange =
    (field: keyof IBANForm) =>
    (value: string): void => {
      setIbanForm((prev) => {
        const nextForm = {
          ...prev,
          [field]: value,
        };

        const formFilledOut = hasNoEmptyFields({
          form: nextForm,
          opts: { skipFields: ['bic'] },
        });

        if (formFilledOut) {
          const ibanWithCountry = `${recipientCountry}${nextForm.iban}`;
          const result = ibanFormValidator(
            { ...nextForm, iban: ibanWithCountry },
            allowUK,
          );
          if (!result.isValid) {
            setFormErrors(
              result.errors.reduce<Record<string, string>>(
                (acc, { field, message }) => {
                  acc[field as string] = message;
                  return acc;
                },
                {},
              ),
            );
            handleFormComplete(false);
          } else {
            setFormErrors({});
            handleFormComplete(true);
            handleFormChange({
              id: formID,
              form: {
                ...nextForm,
                iban: ibanWithCountry,
              },
            });
          }
        }

        return nextForm;
      });
    };

  return (
    <div className={styles.container}>
      <Typography variant={'h5'}>{t('sunshine.label.iban_form')}</Typography>
      <TextInput
        handleChange={handleFieldChange('accountHolderName')}
        label={t('sunshine.label.account_holder_name')}
        value={ibanForm.accountHolderName}
        errorMsg={formErrors.accountHolderName ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('bic')}
        labelFormat={'none'}
        label={t('sunshine.label.bic')}
        value={ibanForm?.bic ?? ''}
        errorMsg={formErrors.bic ?? ''}
      />
      <div className={styles.ibanRow}>
        <div className={styles.ibanPrefixField}>
          <div className={styles.ibanPrefixLabelWrapper}>
            <p
              className={`${styles.ibanPrefixLabelText} ${
                !isIbanFocused && styles.hideIbanPrefixLabelText
              }`}
            >
              {t('sunshine.label.country_code')}
            </p>
          </div>
          <div className={styles.ibanPrefix}>{recipientCountry}</div>
        </div>
        <TextInput
          handleChange={handleFieldChange('iban')}
          labelFormat={'none'}
          label={t('sunshine.label.iban_account_number')}
          value={ibanForm.iban}
          errorMsg={formErrors.iban ?? ''}
          onFocus={() => setIsIbanFocused(true)}
          onBlur={() => setIsIbanFocused(false)}
        />
      </div>
      <TextInput
        handleChange={handleFieldChange('city')}
        label={t('sunshine.label.city')}
        value={ibanForm.city}
        errorMsg={formErrors.city ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('addressLine')}
        label={t('sunshine.label.address')}
        value={ibanForm.addressLine}
        errorMsg={formErrors.addressLine ?? ''}
      />
      <TextInput
        handleChange={handleFieldChange('postalCode')}
        label={t('sunshine.label.postal_code')}
        value={ibanForm.postalCode}
        errorMsg={formErrors.postalCode ?? ''}
      />
    </div>
  );
};

export default AddPayoutAccountIBANForm;
